import { OrderRequirementsSection } from "./OrderRequirementsSection";
import { DriverOrderReview } from "../DriverOrders/DriverOrderReview";
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useMemo, useState, type FC } from "react";
import firebase from "firebase/compat/app";
import { Driver, Order, Vehicle } from "../../models";
import {
  SimpleList,
  InfoMessageAtom,
  Button,
  ImageList,
  StatusBadge,
  RouteTimeline,
} from "@components";
import { useTranslation } from "react-i18next";
import { getCargoTotals } from "../../helpers/orderData";
import { invoiceOrderIds } from "../../helpers/invoiceOrders";
import { mapEnhancedRouteToTimelineStop } from "../../helpers/routeMappers";
import {
  isHiddenDefaultPickup,
  isDefaultLocationPickup,
} from "../../helpers/defaultPickup";
import { formatAddressOneLine } from "../../utils/address";
import { inferPriceRequest } from "../../helpers/orderFormat";

import { useWorkspace } from "../../context/WorkspaceContext";
import { useWorkspaceData } from "@data";
import {
  HiOutlineCalendar,
  HiCheck,
  HiOutlineArrowRight,
  HiOutlineDocumentText,
  HiTrash,
} from "react-icons/hi";
import { Tooltip } from "flowbite-react";
import { FaRegCopy } from "react-icons/fa";
import { Timestamp } from "../../types";
import { Link, useNavigate } from "react-router-dom";
import { getCargoImageUrls } from "../../utils/imageUtils";
import { checkPricing, getInvoices } from "../../api/client";
import { isDriverCompanyVatRegistered } from "../../utils/pricing";
import { useUserSession } from "../../context/UserContext";
import { useRoute } from "../../hooks";
import Invoice from "../../models/Invoice";
import { OrderParticipantCards } from "./OrderParticipantCards";
import { OrderPriceDetails, PricingData } from "./OrderPriceDetails";
import { SignatureCard } from "../Cards/SignatureCard";
import { IOrderAttachment, OrderStatus } from "../../types/order/IOrder";
import { OrderAttachmentUpload } from "../molecules/OrderAttachments";
import { firestore } from "../../firebaseConfig";
import {
  cardStat,
  sectionDivider,
  textBody,
  cn,
  DetailHeader,
  Text,
  textSectionTitle,
  textSectionTitleLg,
  Stack,
  Inline,
} from "@design-system";

const isImageAttachment = (attachment: IOrderAttachment) => {
  if (attachment.contentType?.startsWith("image/")) return true;

  const extension = attachment.name?.split(".").pop()?.toLowerCase();
  return (
    !!extension &&
    ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)
  );
};

const formatFileSize = (size: number) => {
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const dedupeAttachments = (attachments: IOrderAttachment[]) => {
  const seen = new Set<string>();
  return attachments.filter((attachment) => {
    const key = attachment.id || attachment.storagePath || attachment.url;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const isDeletedAttachment = (attachment: IOrderAttachment) =>
  !!attachment.deletedAt || !!attachment.deletedBy;

export interface OrderDetailsProps {
  order: Order | undefined;
  drivers: Driver[];
  editCargo?: () => void; // Optional prop for backward compatibility
}

export const OrderDetails: FC<OrderDetailsProps> = (props) => {
  const { t } = useTranslation(["common", "orders"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const [driver, setDriver] = useState<Driver | undefined>();
  const [vehicle, setVehicle] = useState<Vehicle | undefined>();
  const [hasCopied, setHasCopied] = useState(false);
  const [hasCopiedOrderId, setHasCopiedOrderId] = useState(false);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [isCompanyVatRegistered, setIsCompanyVatRegistered] = useState<
    boolean | null
  >(null);

  const [pricingError, setPricingError] = useState<string | null>(null);
  const isUnpricedOrder = props.order ? inferPriceRequest(props.order) : false;
  // Vehicles and companies come from the centrally-loaded data store.
  const { vehicles, companies, loading, workspaceSettings } = useWorkspaceData();
  const isCompaniesLoading = loading.companies;
  const routeQuery = useRoute(props.order?.id);
  const navigate = useNavigate();
  const isQueryLoading = useMemo(() => loading.vehicles, [loading.vehicles]);
  const [orderInvoice, setOrderInvoice] = useState<Invoice | null>(null);
  const [localAttachments, setLocalAttachments] = useState<IOrderAttachment[]>(
    [],
  );
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>(
    [],
  );
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    string | null
  >(null);
  const [attachmentDeleteError, setAttachmentDeleteError] = useState<
    string | null
  >(null);

  const signatureData = useMemo(() => {
    if (!props.order?.route) return [];

    return props.order.route
      .filter((stop) => stop.signatureUrl)
      .map((stop) => {
        const signedBy = stop.signedBy;
        const deliveryComment = stop.deliveryComment;
        const location = formatAddressOneLine(stop.location);

        const isPickup = props.order?.cargo.some(
          (c) => c.itinerary?.pickupStopNumber === stop.stopNumber,
        );
        const isDelivery = props.order?.cargo.some(
          (c) => c.itinerary?.deliveryStopNumber === stop.stopNumber,
        );

        let stopType = "";
        if (isPickup && isDelivery) {
          stopType = t("common:pickup_and_delivery");
        } else if (isPickup) {
          stopType = t("common:pickup");
        } else if (isDelivery) {
          stopType = t("common:delivery");
        }

        return {
          signatureUrl: stop.signatureUrl!,
          signedBy,
          deliveryComment,
          location,
          stopType,
        };
      });
  }, [props.order?.route, props.order?.cargo, t]);

  // Calculate values for conditional display (canonical, quantity-weighted)
  const totalWeight = useMemo(
    () => getCargoTotals(props.order?.cargo).totalWeight,
    [props.order?.cargo],
  );
  const totalVolume = useMemo(
    () => getCargoTotals(props.order?.cargo).totalVolume,
    [props.order?.cargo],
  );
  const deliveryDeviations = useMemo(
    () =>
      (props.order?.cargo ?? []).filter(
        (cargo) =>
          cargo.status !== "Cancelled" &&
          cargo.deliveredAt != null &&
          typeof cargo.deliveredQuantity === "number" &&
          cargo.deliveredQuantity < cargo.quantity,
      ),
    [props.order?.cargo],
  );
  const hasDeliveryDeviations = deliveryDeviations.length > 0;

  // Track which sections are visible to manage separators intelligently
  const hasLastSection = totalWeight > 0 || totalVolume > 0;
  const hasCommentsSection = !!props.order?.comment?.length;
  const hasSignaturesSection = signatureData.length > 0;
  const orderAttachments = dedupeAttachments([
    ...(props.order?.attachments ?? []),
    ...localAttachments,
  ]).filter(
    (attachment) =>
      !isDeletedAttachment(attachment) &&
      !deletedAttachmentIds.includes(attachment.id),
  );
  const attachmentImageUrls = orderAttachments
    .filter(isImageAttachment)
    .map((attachment) => attachment.url);
  const fileAttachments = orderAttachments.filter(
    (attachment) => !isImageAttachment(attachment),
  );
  const imageUrls = [
    ...getCargoImageUrls(props.order?.cargo),
    ...attachmentImageUrls,
  ];

  useEffect(() => {
    setLocalAttachments([]);
    setDeletedAttachmentIds([]);
    setAttachmentDeleteError(null);
  }, [props.order?.id]);

  const handleDeleteAttachment = async (attachment: IOrderAttachment) => {
    if (!props.order || !attachment.id) return;

    const confirmed = window.confirm(t("orders:attachments.delete_confirm"));
    if (!confirmed) return;

    setDeletingAttachmentId(attachment.id);
    setAttachmentDeleteError(null);

    try {
      const orderRef = firestore
        .collection(Order.collectionName)
        .doc(props.order.id);
      const deletedAt = Timestamp.now();
      const deletedBy = userSession?.user.email ?? null;

      await firestore.runTransaction(async (transaction) => {
        const orderSnapshot = await transaction.get(orderRef);
        const attachments = (orderSnapshot.data()?.attachments ??
          []) as IOrderAttachment[];
        const updatedAttachments = attachments.map((currentAttachment) =>
          currentAttachment.id === attachment.id
            ? {
                ...currentAttachment,
                deletedAt,
                deletedBy,
              }
            : currentAttachment,
        );

        transaction.update(orderRef, {
          attachments: updatedAttachments,
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        });
      });

      setDeletedAttachmentIds((current) => [...current, attachment.id]);
      setLocalAttachments((current) =>
        current.filter(
          (currentAttachment) => currentAttachment.id !== attachment.id,
        ),
      );
    } catch (error) {
      console.error("Failed to delete order attachment:", error);
      setAttachmentDeleteError(
        error instanceof Error
          ? error.message
          : t("orders:attachments.delete_failed"),
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  // Transform route data to work with the existing RouteTimeline component
  const timelineStops = useMemo(() => {
    if (!routeQuery.routeData || routeQuery.routeData.length === 0) {
      return [];
    }

    // Only hide the default pickup when the order also has a visible sibling
    // stop, so an order made up solely of default pickups never renders empty.
    const hasVisibleSibling = routeQuery.routeData.some(
      (stop) => !isDefaultLocationPickup(stop, workspaceSettings),
    );

    return routeQuery.routeData
      .filter(
        (stop) =>
          !(hasVisibleSibling && isHiddenDefaultPickup(stop, workspaceSettings)),
      )
      .map(mapEnhancedRouteToTimelineStop);
  }, [routeQuery.routeData, workspaceSettings]);

  // Route items are updated by the driver flow before the aggregate order
  // status is guaranteed to arrive. Use that realtime route state to advance
  // the status shown in the detail header, while never downgrading a terminal
  // or pre-dispatch order status.
  const displayStatus = useMemo(() => {
    const orderStatus = props.order?.status;
    if (
      !orderStatus ||
      [
        OrderStatus.Draft,
        OrderStatus.New,
        OrderStatus.Completed,
        OrderStatus.Cancelled,
      ].includes(orderStatus)
    ) {
      return orderStatus;
    }

    const routeStatuses = routeQuery.routeData.map((route) => route.status);
    if (routeStatuses.length === 0) return orderStatus;

    if (routeStatuses.every((status) => status === OrderStatus.Completed)) {
      return OrderStatus.Completed;
    }

    if (
      routeStatuses.some(
        (status) =>
          status === OrderStatus.Completed || status === OrderStatus.InProgress,
      )
    ) {
      return OrderStatus.InProgress;
    }

    return orderStatus;
  }, [props.order?.status, routeQuery.routeData]);

  const handleNavigateToPlanner = () => {
    if (!props.order || !driver?.email) return;

    const orderDate = props.order.route?.at(0)?.stopDate;
    // toFormattedDate handles both Firestore Timestamps and REST ISO strings
    // (a raw .toDate() call throws on the latter) and yields YYYY-MM-DD for the
    // planner query param; fall back to today when the date is missing.
    const formattedDate =
      Timestamp.toFormattedDate(orderDate, "YYYY-MM-DD") ||
      new Date().toISOString().split("T")[0];

    const encodedDriverEmail = encodeURIComponent(driver.email);
    const plannerUrl = `/planner?tab=route&date=${formattedDate}&driver=${encodedDriverEmail}`;
    navigate(plannerUrl);
  };

  // Fetch order price using the pricing API
  useEffect(() => {
    let isMounted = true;

    const fetchOrderPrice = async () => {
      if (
        !props.order ||
        !userSession?.token?.accessToken ||
        !activeWorkspace?.workspaceId
      ) {
        return;
      }

      if (inferPriceRequest(props.order)) {
        setPricingData(null);
        setPricingError(null);
        return;
      }

      setPricingError(null);
      try {
        const response = await checkPricing(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          props.order,
          isCompanyVatRegistered ?? true,
        );

        if (!isMounted) return; // Prevent state update if component unmounted

        if (response.success && response.data) {
          setPricingData({
            amounts: response.data.amounts || {
              totalAmount: 0,
              totalAmountWithoutVat: 0,
              totalVatAmount: 0,
            },
            items: response.data.items || [],
          });
        } else {
          setPricingData(null);
          setPricingError(
            t("common:error_loading_price", "Kunne ikke laste prisdata"),
          );
        }
      } catch (error) {
        if (!isMounted) return; // Prevent state update if component unmounted
        setPricingData(null);
        setPricingError(
          t("common:error_loading_price", "Kunne ikke laste prisdata"),
        );
      } finally {
        // no-op
      }
    };

    fetchOrderPrice();

    return () => {
      isMounted = false; // Cleanup flag
    };
  }, [
    props.order,
    userSession?.token?.accessToken,
    activeWorkspace?.workspaceId,
    t,
    isCompanyVatRegistered,
  ]);

  useEffect(() => {
    if (!isQueryLoading) {
      if (props.drivers) {
        setDriver(props.drivers.find((x) => x.email === props.order?.driverId));
      }
      if (vehicles && driver) {
        setVehicle(vehicles.find((x) => x.id === driver.vehicleId));
      }
    }
    setHasCopied(false);
  }, [isQueryLoading, props.drivers, vehicles, driver, props.order]);

  // Determine VAT registration based on driver's company
  useEffect(() => {
    if (!isCompaniesLoading) {
      const vatFlag = isDriverCompanyVatRegistered(
        props.order?.driverId,
        props.drivers,
        companies,
      );
      setIsCompanyVatRegistered(vatFlag);
    }
  }, [props.order?.driverId, props.drivers, companies, isCompaniesLoading]);

  // Firebase Console helpers
  const firebaseProjectId =
    (process.env.REACT_APP_FIREBASE_PROJECT_ID as string) || "logibud-dev-app";

  const openFirebaseOrderInConsole = () => {
    if (!props.order?.id) return;
    const url = `https://console.firebase.google.com/u/1/project/${firebaseProjectId}/firestore/databases/-default-/data/~2Forders~2F${props.order.id}`;
    window.open(url, "_blank", "noopener");
  };

  const openRoutesQueryForOrderId = () => {
    if (!props.order?.id) return;
    const routesDocSegment = "~2Froutes~2F7dSezmNDFlfsaQ16Hg8D";
    const queryString = `2|LIM|3/100|WH|1|7/orderId|EQ|STR|20/${props.order.id}`;
    const encodedQuery = encodeURIComponent(queryString);
    const scopeName = encodeURIComponent("/routes");
    const url = `https://console.firebase.google.com/u/1/project/${firebaseProjectId}/firestore/databases/-default-/data/${routesDocSegment}?view=query-view&query=${encodedQuery}&scopeType=collection&scopeName=${scopeName}`;
    window.open(url, "_blank", "noopener");
  };

  useEffect(() => {
    const fetchOrderInvoice = async () => {
      if (
        !props.order ||
        !activeWorkspace?.workspaceId ||
        !userSession?.token?.accessToken
      )
        return;
      try {
        const invoices = await getInvoices(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
        );
        if (Array.isArray(invoices)) {
          const found = invoices.find(
            (inv: any) => !inv.isCreditInvoice && inv.lifecycleStatus !== "discarded" && invoiceOrderIds(inv).includes(props.order!.id),
          );
          if (found) setOrderInvoice(new Invoice(found));
          else setOrderInvoice(null);
        }
      } catch (e) {
        setOrderInvoice(null);
      }
    };
    fetchOrderInvoice();
  }, [
    props.order,
    activeWorkspace?.workspaceId,
    userSession?.token?.accessToken,
  ]);

  return (
    <>
      <DetailHeader compact>
        <Stack gap={2}>
          <Inline gap={4}>
            <Text as="p" variant="detail-title">
              {t("common:order_number")} {props.order?.orderNumber ?? "-"}
            </Text>
            {!hasCopied ? (
              <Tooltip content={t("common:copy")}>
                <div
                  className="bg-none rounded-md relative"
                  style={{ width: "34px", height: "34px" }}
                >
                  <FaRegCopy
                    className="w-6 h-6 dark:text-white cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    onClick={() => {
                      // Copy orderNumber if available, else copy id
                      const textToCopy =
                        props.order?.orderNumber ?? props.order?.id ?? "";
                      navigator.clipboard.writeText(textToCopy);
                      setHasCopied(true);
                    }}
                  />
                </div>
              </Tooltip>
            ) : (
              <div className="flex items-center text-muted dark:text-text">
                <HiCheck className="mr-2 h-5 w-5" />
                <p>{t("common:copied")}</p>
              </div>
            )}
            {(displayStatus === OrderStatus.InProgress ||
              displayStatus === OrderStatus.Completed) && (
              <StatusBadge status={displayStatus} />
            )}
            {hasDeliveryDeviations && (
              <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {t("orders:deviations.badge")}
              </span>
            )}
          </Inline>
          {props.order?.isInvoiced && orderInvoice && (
            <Link
              to={`/invoices?id=${orderInvoice.id}`}
              className="flex items-center gap-2 text-lgb-blue-600 dark:text-brand hover:text-lgb-blue-700 dark:hover:text-brand transition-colors"
            >
              <HiOutlineDocumentText className="h-5 w-5" />
              <p className="text-sm underline">
                {t("orders:completed_orders.invoiced")}
              </p>
            </Link>
          )}
          {props.order?.projectNumber && (
            <Inline gap={2} className="text-muted">
              <p className="text-sm">
                {t("common:project_number")}: {props.order.projectNumber}
              </p>
            </Inline>
          )}
          {userSession?.user?.isAdmin && props.order?.id && (
            <Inline gap={2} wrap className="pt-1">
              <Button
                variant="secondary"
                size="xs"
                onClick={openFirebaseOrderInConsole}
                className="inline-flex items-center"
                aria-label="Åpne ordre i Firebase"
              >
                Åpne ordre i Firebase
              </Button>
              <Button
                variant="secondary"
                size="xs"
                onClick={openRoutesQueryForOrderId}
                className="inline-flex items-center"
                aria-label="Rutedata for ordre"
              >
                Rutedata for ordre
              </Button>
              {!hasCopiedOrderId ? (
                <Tooltip content={t("common:copy_order_id", "Kopier ordreId")}>
                  <div
                    className="bg-none rounded-md relative"
                    style={{ width: "28px", height: "28px" }}
                    role="button"
                    tabIndex={0}
                    aria-label={t("common:copy_order_id", "Kopier ordreId")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (props.order?.id) {
                          navigator.clipboard.writeText(props.order.id);
                          setHasCopiedOrderId(true);
                          setTimeout(() => setHasCopiedOrderId(false), 1200);
                        }
                      }
                    }}
                  >
                    <FaRegCopy
                      className="w-5 h-5 dark:text-white cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      onClick={() => {
                        if (!props.order?.id) return;
                        navigator.clipboard.writeText(props.order.id);
                        setHasCopiedOrderId(true);
                        setTimeout(() => setHasCopiedOrderId(false), 1200);
                      }}
                    />
                  </div>
                </Tooltip>
              ) : (
                <div className="flex items-center text-muted dark:text-text text-sm">
                  <HiCheck className="mr-1 h-4 w-4" />
                  <p>{t("common:copied")}</p>
                </div>
              )}
            </Inline>
          )}
        </Stack>
        <Inline gap={2} className="dark:text-text mt-2 xl:mt-0">
          <HiOutlineCalendar size={20} />
          <p>{Timestamp.toDisplayDate(props.order?.route.at(0)?.stopDate)}</p>
        </Inline>
      </DetailHeader>

      {props.order?.id.startsWith("drv_") && <DriverOrderReview key={`${activeWorkspace?.workspaceId}:${props.order.id}`} order={props.order} />}
      <OrderParticipantCards
        customer={props.order?.customer}
        driver={driver}
        showVehicle
        vehicleRegistration={vehicle?.registrationNumber}
      />

      {displayStatus === OrderStatus.New && (
        <div className="my-4">
          <InfoMessageAtom
            variant="warning"
            title={t("orders:new_orders.info_title")}
          >
            <Stack gap={4}>
              <p>{t("orders:new_orders.info_description")}</p>
              <Button
                variant="secondary"
                size="md"
                onClick={handleNavigateToPlanner}
                disabled={!driver?.email}
                aria-label={t("orders:new_orders.go_to_planner_aria")}
                className="self-start"
              >
                <span className="flex items-center">
                  {t("orders:new_orders.go_to_planner")}
                  <HiOutlineArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Button>
            </Stack>
          </InfoMessageAtom>
        </div>
      )}

      {displayStatus === OrderStatus.Scheduled && (
        <div className="my-4">
          <InfoMessageAtom
            variant="info-blue"
            title={t("orders:scheduled_orders.info_title")}
          >
            <p>{t("orders:scheduled_orders.info_description")}</p>
          </InfoMessageAtom>
        </div>
      )}

      {displayStatus === OrderStatus.Cancelled && (
        <div className="my-4">
          <InfoMessageAtom
            variant="error"
            title={t("orders:cancelled_orders.info_title")}
          >
            <p>{t("orders:cancelled_orders.info_description")}</p>
          </InfoMessageAtom>
        </div>
      )}

      {hasDeliveryDeviations && (
        <div className="my-4">
          <InfoMessageAtom
            variant="warning"
            title={t("orders:deviations.title")}
          >
            <Stack gap={4}>
              <p>{t("orders:deviations.description")}</p>
              <div className="space-y-3">
                {deliveryDeviations.map((cargo) => (
                  <div
                    key={cargo.id}
                    className="rounded-md border border-red-200 bg-white p-3 dark:border-red-800 dark:bg-gray-800"
                  >
                    <p className="font-medium text-text">
                      {cargo.descriptionShort || t("common:unnamed_product")}
                    </p>
                    <p className="mt-1 text-sm font-medium text-red-700 dark:text-red-300">
                      {t("orders:deviations.delivered_quantity", {
                        delivered: cargo.deliveredQuantity,
                        expected: cargo.quantity,
                      })}
                    </p>
                    {cargo.seatsEnabled && <p className="text-sm text-secondary">{t("orders:seatCount", {count: cargo.seatCount ?? 0})}</p>}
                    {cargo.comment && (
                      <p className={`mt-2 text-sm ${textBody}`}>
                        {cargo.comment}
                      </p>
                    )}
                    {!!cargo.imgUrls?.length && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {cargo.imgUrls.map((imgUrl, index) => (
                          <a
                            key={`${cargo.id}-deviation-image-${index}`}
                            href={imgUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={t("orders:deviations.open_image")}
                          >
                            <img
                              src={imgUrl}
                              alt=""
                              className="h-16 w-16 rounded-md border border-red-200 object-cover dark:border-red-800"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Stack>
          </InfoMessageAtom>
        </div>
      )}

      {/* Separator before timeline */}
      <div className={sectionDivider}></div>

      <p
        className={cn(
          textSectionTitle,
          "mb-4 flex items-center justify-between",
        )}
      >
        {t("common:route")}
        {props.order?.id && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              props.order?.id && navigate(`/map?orderId=${props.order.id}`)
            }
            className="inline-flex items-center"
          >
            {t("common:open_route_map", "Åpne rutekart")}
          </Button>
        )}
      </p>

      {routeQuery.isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      ) : timelineStops.length > 0 ? (
        <RouteTimeline
          stops={timelineStops}
          cargo={props.order?.cargo || []} // Pass actual cargo data to show pickup times
        />
      ) : (
        // Fallback to order route data if route collection is empty
        <RouteTimeline
          stops={
            props.order?.route
              ?.filter((route) => {
                // Keep the default pickup unless the order has a visible sibling.
                const hasVisibleSibling = (props.order?.route ?? []).some(
                  (stop) => !isDefaultLocationPickup(stop, workspaceSettings),
                );
                return !(
                  hasVisibleSibling &&
                  isHiddenDefaultPickup(route, workspaceSettings)
                );
              })
              .map((route) => {
              // Determine if this stop is a pickup by checking if any cargo has this stop as pickup
              const isPickup =
                props.order?.cargo?.some(
                  (cargo) =>
                    cargo.itinerary?.pickupStopNumber === route.stopNumber,
                ) ?? route.stopNumber === 0; // Fallback to first stop if no cargo data

              return {
                stopNumber: route.stopNumber,
                isPickup,
                location: {
                  addressLine: route.location.addressLine,
                  displayName: route.location.displayName,
                  city: route.location.city,
                  postCode: route.location.postCode,
                },
                stopDate: route.stopDate,
                status: route.status || "New",
                completedAt:
                  route.status === "Completed" ? route.stopDate : null,
                signatureUrl: route.signatureUrl,
                timing: route.timing,
              };
            }) || []
          }
          cargo={props.order?.cargo || []} // Pass actual cargo data to show pickup times
        />
      )}

      {props.order && activeWorkspace?.workspaceId && (
        <OrderRequirementsSection
          key={`${activeWorkspace.workspaceId}:${props.order.id}`}
          workspaceId={activeWorkspace.workspaceId}
          orderId={props.order.id}
        />
      )}

      {/* Separator after timeline */}
      <div className={sectionDivider}></div>

      {/* Last Section - only show if there's weight or volume */}
      {hasLastSection && (
        <>
          <p className={cn(textSectionTitle, "mb-3")}>{t("common:cargo")}</p>

          {/* Cargo Info Boxes */}
          <div className="flex space-x-4 mb-6">
            {/* Total weight - only show if > 0 */}
            {totalWeight > 0 && (
              <div className={`flex-1 ${cardStat}`}>
                <div className="text-sm font-medium text-text mb-1">
                  {t("common:total_weight")}
                </div>
                <div className="text-sm text-text">{totalWeight} kg</div>
              </div>
            )}

            {/* Total volume - only show if > 0 */}
            {totalVolume > 0 && (
              <div className={`flex-1 ${cardStat}`}>
                <div className="text-sm font-medium text-text mb-1">
                  {t("common:total_volume")}
                </div>
                <div className="text-sm text-text">
                  {totalVolume.toFixed(3)} m³
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Comments and Project Number - only show if they exist */}
      {hasCommentsSection && (
        <>
          {/* Only show separator if Last section was shown */}
          {hasLastSection && <div className={sectionDivider}></div>}
          <SimpleList
            items={[
              ...(props.order?.comment.length
                ? [
                    {
                      title: t("common:comment"),
                      value: props.order?.comment ?? "",
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}

      {/* Separator before files and images - only if we have content above */}
      {(hasLastSection || hasCommentsSection) && (
        <div className={sectionDivider}></div>
      )}

      <div className="my-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className={cn(textSectionTitleLg, "mb-0 sm:mb-4")}>
            {t("orders:attachments.section_title")}
          </p>
          {props.order && activeWorkspace?.workspaceId && (
            <OrderAttachmentUpload
              order={props.order}
              workspaceId={activeWorkspace.workspaceId}
              onUploaded={(attachments) =>
                setLocalAttachments((current) =>
                  dedupeAttachments([...current, ...attachments]),
                )
              }
            />
          )}
        </div>
        {attachmentDeleteError && (
          <p className="mb-3 text-sm text-red-600 dark:text-red-400">
            {attachmentDeleteError}
          </p>
        )}
        <p className={cn(textSectionTitle, "mb-3")}>{t("common:images")}</p>
        <ImageList
          imgUrls={imageUrls}
          deletableImgUrls={attachmentImageUrls}
          deletingImgUrl={
            orderAttachments.find(
              (attachment) => attachment.id === deletingAttachmentId,
            )?.url
          }
          deleteAriaLabel={t("orders:attachments.delete")}
          onDelete={(imgUrl) => {
            const attachment = orderAttachments.find(
              (candidate) => candidate.url === imgUrl,
            );
            if (attachment) void handleDeleteAttachment(attachment);
          }}
          key={`images-${props.order?.id}-${props.order?.lastUpdated?.seconds || "no-timestamp"}`}
        />

        <div className="mt-6">
          <p className={cn(textSectionTitle, "mb-3")}>
            {t("orders:attachments.files")}
          </p>
          {fileAttachments.length === 0 ? (
            <p className={textBody}>{t("orders:attachments.no_files")}</p>
          ) : (
            <>
              <div className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                {fileAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                  >
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-w-0 flex-1 items-center gap-3 text-gray-900 hover:text-lgb-blue-500 dark:text-white dark:hover:text-lgb-blue-200"
                    >
                      <HiOutlineDocumentText className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
                      <span className="truncate">{attachment.name}</span>
                    </a>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(attachment.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(attachment)}
                        disabled={deletingAttachmentId === attachment.id}
                        className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                        aria-label={t("orders:attachments.delete")}
                      >
                        <HiTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Signatures Section */}
      {hasSignaturesSection && (
        <>
          <div className={sectionDivider}></div>
          <div className="my-8">
            <p className={cn(textSectionTitle, "mb-3")}>
              {t("common:signatures")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {signatureData.map((signature, index) => (
                <SignatureCard
                  key={index}
                  signatureUrl={signature.signatureUrl}
                  signedBy={signature.signedBy}
                  location={signature.location}
                  deliveryComment={signature.deliveryComment}
                  stopType={signature.stopType}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Price Details Section - moved to bottom */}
      {isUnpricedOrder && (
        <>
          <div className={sectionDivider}></div>
          <p className={cn(textSectionTitle, "mb-3")}>
            {t("common:price_details", "Prisdetaljer")}
          </p>
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t(
                "common:unpriced_transport_order",
                "Transportprisen er ikke satt ennå. Velg et standard transportprodukt eller sett prisen når ordren godkjennes.",
              )}
            </p>
          </div>
        </>
      )}
      {pricingError && (
        <>
          <div className={sectionDivider}></div>
          <p className={cn(textSectionTitle, "mb-3")}>
            {t("common:price_details", "Prisdetaljer")}
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-600 dark:text-red-400 text-sm">
              {pricingError}
            </p>
          </div>
        </>
      )}
      {pricingData && pricingData.items && pricingData.items.length > 0 && (
        <>
          <div className={sectionDivider}></div>
          <p className={cn(textSectionTitle, "mb-3")}>
            {t("common:price_details", "Prisdetaljer")}
          </p>

          <OrderPriceDetails
            items={pricingData.items}
            amounts={pricingData.amounts}
          />
        </>
      )}
    </>
  );
};
